import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Dices, Loader2, Lock, X } from "lucide-react";
import { renameTeam, updateTeamIdentity } from "../../api/teamApi";
import {
  TEAM_COLORS,
  TEAM_EMBLEMS,
  TEAM_NAME_MAX,
  formatRelativeTime,
  getContrastColor,
  getTeamColor,
  getTeamEmblemKey,
  suggestTeamName,
  validateTeamName,
} from "./teamIdentity";
import { TeamEmblem } from "./TeamUI";

const normalize = (value) => (value || "").trim().replace(/\s+/g, " ");

/**
 * Bottom sheet (mobile) / dialog (desktop) to rename a team and pick its
 * emblem and color. Name and identity are saved through separate endpoints;
 * only what changed is sent.
 */
const TeamCustomizeModal = ({ team, userId, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const nameHelpId = useId();
  const inputRef = useRef(null);

  const initialName = team.isAutoName ? "" : team.teamName;
  const [name, setName] = useState(initialName);
  const [emblem, setEmblem] = useState(getTeamEmblemKey(team));
  const [color, setColor] = useState(getTeamColor(team));
  const [nameError, setNameError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const renameLockedUntil =
    team.nextRenameAt && new Date(team.nextRenameAt).getTime() > Date.now() ? team.nextRenameAt : null;
  const nameChanged = !renameLockedUntil && normalize(name) !== "" && normalize(name) !== team.teamName;
  const identityChanged = emblem !== getTeamEmblemKey(team) || color !== getTeamColor(team);
  const preview = { ...team, emblem, color, teamName: normalize(name) || team.teamName };

  useEffect(() => {
    const previous = document.activeElement;
    (renameLockedUntil ? null : inputRef.current)?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    if (nameChanged) {
      const error = validateTeamName(name, t);
      setNameError(error);
      if (error) {
        inputRef.current?.focus();
        return;
      }
    }
    if (!nameChanged && !identityChanged) {
      onClose();
      return;
    }

    setSaving(true);
    let latest = team;
    try {
      if (nameChanged) {
        latest = (await renameTeam(team.id, userId, normalize(name))).team;
      }
      if (identityChanged) {
        latest = (await updateTeamIdentity(team.id, userId, { emblem, color })).team;
      }
      onSaved(latest, { renamed: nameChanged });
    } catch (error) {
      // Keep whatever did save (e.g. rename succeeded, identity failed).
      if (latest !== team) onSaved(latest, { renamed: nameChanged, keepOpen: true });
      if (error.status === 400 && nameChanged && latest === team) {
        setNameError(error.message);
        inputRef.current?.focus();
      } else {
        setFormError(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-[#120239]/70 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        className="relative max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 shadow-2xl sm:max-w-md sm:rounded-3xl motion-safe:animate-in motion-safe:slide-in-from-bottom-8 motion-safe:duration-300"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <TeamEmblem team={preview} size="md" />
            <div className="min-w-0">
              <h2 id={titleId} className="text-xl font-bold text-[#120239]">
                {t("customizeTeam", { defaultValue: "Customize your team" })}
              </h2>
              <p className="truncate text-sm text-gray-500">{preview.teamName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t("close", { defaultValue: "Close" })}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
          >
            <X size={20} />
          </button>
        </div>

        {formError && (
          <div role="alert" className="mb-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 ring-1 ring-rose-200">
            {formError}
          </div>
        )}

        {/* Name */}
        <div>
          <label htmlFor={`${titleId}-name`} className="text-sm font-semibold text-gray-800">
            {t("teamName", { defaultValue: "Team name" })}
          </label>
          <div className="mt-2 flex gap-2">
            <input
              id={`${titleId}-name`}
              ref={inputRef}
              type="text"
              value={name}
              maxLength={TEAM_NAME_MAX}
              disabled={Boolean(renameLockedUntil) || saving}
              placeholder={team.isAutoName ? team.teamName : ""}
              onChange={(event) => {
                setName(event.target.value);
                if (nameError) setNameError(null);
              }}
              onBlur={() => nameChanged && setNameError(validateTeamName(name, t))}
              aria-invalid={Boolean(nameError)}
              aria-describedby={nameHelpId}
              className={`min-h-[44px] w-full rounded-2xl border px-4 text-base outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100 disabled:text-gray-500 ${
                nameError ? "border-rose-400" : "border-gray-300"
              }`}
            />
            <button
              type="button"
              disabled={Boolean(renameLockedUntil) || saving}
              onClick={() => {
                setName(suggestTeamName(name));
                setNameError(null);
              }}
              aria-label={t("suggestName", { defaultValue: "Suggest a name" })}
              title={t("suggestName", { defaultValue: "Suggest a name" })}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-2xl bg-orange-100 text-orange-700 transition hover:bg-orange-200 active:scale-95 disabled:opacity-50"
            >
              <Dices size={20} />
            </button>
          </div>
          <div id={nameHelpId} className="mt-1.5 flex justify-between gap-3 text-xs">
            {renameLockedUntil ? (
              <span className="inline-flex items-center gap-1 text-gray-500">
                <Lock size={12} aria-hidden="true" />
                {t("renameLocked", {
                  defaultValue: "You can rename again {{when}}",
                  when: formatRelativeTime(renameLockedUntil, i18n.language),
                })}
              </span>
            ) : nameError ? (
              <span className="font-medium text-rose-600" role="alert">{nameError}</span>
            ) : (
              <span className="text-gray-500">
                {t("renameHint", { defaultValue: "Visible to all members. You can rename once a day." })}
              </span>
            )}
            {!renameLockedUntil && (
              <span className="shrink-0 tabular-nums text-gray-400">
                {normalize(name).length}/{TEAM_NAME_MAX}
              </span>
            )}
          </div>
        </div>

        {/* Emblem */}
        <fieldset className="mt-6">
          <legend className="text-sm font-semibold text-gray-800">
            {t("teamEmblem", { defaultValue: "Emblem" })}
          </legend>
          <div className="mt-2 grid grid-cols-6 gap-2" role="radiogroup">
            {Object.entries(TEAM_EMBLEMS).map(([key, Icon]) => {
              const selected = key === emblem;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={key}
                  disabled={saving}
                  onClick={() => setEmblem(key)}
                  className={`inline-flex aspect-square min-h-[44px] items-center justify-center rounded-2xl transition active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 ${
                    selected ? "shadow-md ring-2 ring-offset-2 ring-[#120239]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  style={selected ? { backgroundColor: color, color: getContrastColor(color) } : undefined}
                >
                  <Icon size={20} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Color */}
        <fieldset className="mt-6">
          <legend className="text-sm font-semibold text-gray-800">
            {t("teamColor", { defaultValue: "Color" })}
          </legend>
          <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
            {TEAM_COLORS.map((swatch) => {
              const selected = swatch === color;
              return (
                <button
                  key={swatch}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={swatch}
                  disabled={saving}
                  onClick={() => setColor(swatch)}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
                    selected ? "ring-2 ring-offset-2 ring-[#120239]" : ""
                  }`}
                  style={{ backgroundColor: swatch, color: getContrastColor(swatch) }}
                >
                  {selected && <Check size={18} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-8 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[48px] flex-1 rounded-full border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50"
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="submit"
            disabled={saving || (!nameChanged && !identityChanged)}
            className="inline-flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-full bg-[#FF9200] font-bold text-[#120239] shadow-md transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
            {t("saveTeam", { defaultValue: "Save team" })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamCustomizeModal;
